import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase/client'
import { User as SupabaseUser } from '@supabase/supabase-js'
import { toast } from 'sonner'

// Novo tipo para refletir o usuário do banco
export interface AppUser {
  id: string
  name: string
  address?: string | null
  phone?: string | null
  created_at?: string
  role: string
  email: string
}

export interface AuthContextType {
  user: AppUser | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<void>
  signOut: () => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithGithub: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider')
  }
  return context
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(null)
  const [loading, setLoading] = useState(true)
  // Usa a instância singleton do Supabase

  useEffect(() => {
    let mounted = true
    let isInitializing = true

    const loadUserData = async (userId: string, email: string) => {
      try {
        const { data: userData, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', userId)
          .single()
        
        if (error && error.code !== 'PGRST116') {
          console.error('Erro ao buscar dados do usuário:', error)
          if (mounted) setUser(null)
          return
        }
        
        if (mounted && userData) {
          setUser({
            ...userData,
            email: email,
          })
        } else if (mounted) {
          setUser(null)
        }
      } catch (error) {
        console.error('Erro ao carregar dados do usuário:', error)
        if (mounted) setUser(null)
      }
    }

    // Quando a aba volta ao foco, força refresh do token JWT.
    // Browsers throttle timers em abas em background, o que pode
    // fazer o auto-refresh do Supabase perder a janela de renovação.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.startAutoRefresh()
        supabase.auth.refreshSession().then(({ data: { session }, error }) => {
          if (!mounted) return
          if (error) {
            console.error('Erro ao refreshar sessão:', error)
            return
          }
          if (!session) {
            setUser(null)
          }
        })
      } else {
        supabase.auth.stopAutoRefresh()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return

      if (event === 'INITIAL_SESSION') {
        if (session?.user) {
          await loadUserData(session.user.id, session.user.email || '')
        } else {
          setUser(null)
        }
        isInitializing = false
        if (mounted) {
          setLoading(false)
        }
        return
      }

      if (isInitializing) return

      if (event === 'TOKEN_REFRESHED') {
        return
      }

      if (session?.user) {
        await loadUserData(session.user.id, session.user.email || '')
      } else {
        setUser(null)
      }
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        throw error
      }

      toast.success('Login realizado com sucesso!')
    } catch (error) {
      toast.error('Erro ao fazer login. Verifique suas credenciais.')
      throw error
    }
  }

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          },
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      })

      if (error) {
        throw error
      }

      toast.success('Conta criada com sucesso!')
    } catch (error) {
      toast.error('Erro ao criar conta. Tente novamente.')
      throw error
    }
  }

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        throw error
      }

      toast.success('Logout realizado com sucesso!')
      // Navegação será feita pelo componente que chama signOut
    } catch (error) {
      toast.error('Erro ao fazer logout.')
      throw error
    }
  }

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
      })

      if (error) {
        throw error
      }
    } catch (error) {
      toast.error('Erro ao fazer login com Google.')
      throw error
    }
  }

  const signInWithGithub = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'github',
      })

      if (error) {
        throw error
      }
    } catch (error) {
      toast.error('Erro ao fazer login com GitHub.')
      throw error
    }
  }

  const value = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithGithub,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
} 